import { useQuery, useMutation, useQueryClient } from 'react-query';
import { expenseApi } from '../services/api';
import { useToast } from './useToast';
import type { ExpenseCreate, ExpenseUpdate, ExpenseFilter, ExpensePaginationParams, ExpenseListResponse } from '../types';

export function useExpenses(
  filters?: ExpenseFilter,
  pagination?: ExpensePaginationParams
) {
  return useQuery<ExpenseListResponse>(
    ['expenses', filters, pagination],
    () => expenseApi.getAll(filters, pagination)
  );
}

export function useExpense(expenseId: string) {
  return useQuery(['expenses', expenseId], () => expenseApi.getById(expenseId), {
    enabled: !!expenseId,
  });
}

export function useCreateExpense() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  
  return useMutation((data: ExpenseCreate) => expenseApi.create(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['expenses']);
      queryClient.invalidateQueries(['projects']);
      showSuccess('Gasto creado exitosamente');
    },
    onError: (error) => {
      showError(error, 'Error al crear el gasto');
    },
  });
}

export function useUpdateExpense() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  
  return useMutation(
    ({ expenseId, data }: { expenseId: string; data: ExpenseUpdate }) =>
      expenseApi.update(expenseId, data),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(['expenses']);
        queryClient.invalidateQueries(['expenses', variables.expenseId]);
        queryClient.invalidateQueries(['projects']);
        showSuccess('Gasto actualizado exitosamente');
      },
      onError: (error) => {
        showError(error, 'Error al actualizar el gasto');
      },
    }
  );
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  
  return useMutation((expenseId: string) => expenseApi.delete(expenseId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['expenses']);
      queryClient.invalidateQueries(['projects']);
      showSuccess('Gasto eliminado exitosamente');
    },
    onError: (error) => {
      showError(error, 'Error al eliminar el gasto');
    },
  });
}
